import React from "react";
import { isMobile, isAndroid, isIOS } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";

import { Box, Flex, Heading, VStack, useBreakpointValue } from "@chakra-ui/react";

import faqFormal from "assets/images/faq-formal@2x.png";
import supportFormal from "assets/images/support-formal@2x.png";
import { BottomBanner as AddToHomeScreenShortcutBottomBanner } from "components/HomeView/AddToHomeScreenShortcut";
import ESimBanner from "components/HomeView/ESimBanner";
import JourneysLatest from "components/Journeys/JourneysLatest";
import { PAGE_PATHS } from "config/routes";
import { useFeatureFlags } from "contexts/FeatureFlagsContext/FeatureFlagsContext";
import { useLocale } from "contexts/LocaleContext/LocaleContextProvider";
import { useZowie } from "contexts/ZowieContext";
import ActionCard from "elements/ActionCard";
import { ClaimEnquiry } from "models/Claim";
import { Journey } from "models/Journey";
import { LoungePass } from "models/LoungePass";
import UserProfile from "models/UserProfile";
import { airhelpUtils } from "utils";
import { useTrackEvent } from "utils/tracking/hooks";

import ClaimsLatest from "./ClaimsLatest";
import HowCanWeHelp from "./HowCanWeHelp";
import LoungeExperienceTile from "./LoungeExperienceTile";
import PerkServices from "./PerksServices";
import UpsellEssential from "./UpsellEssential";

interface IComponent {
  userProfile: UserProfile;
  journeys: Journey[];
  loungePasses: LoungePass[];
  claims: ClaimEnquiry[];
}

const HomeView: React.FC<IComponent> = ({ userProfile, journeys, claims, loungePasses }) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { trackCtaClick } = useTrackEvent();
  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags();

  const { newAhpPlans } = featureFlags;
  const { policy: userPolicy } = userProfile;
  const { esim, carRental, airportTransfer } = userPolicy.perks;

  const showMembershipEssentialUpsell =
    userPolicy.dashboard?.upsellEnabled && !userPolicy.membership && !!userPolicy.trip;

  const showLoungeExperienceTile = loungePasses.length > 0;
  const showSupport = userPolicy.coverage.covered;

  const showESimBanner = !newAhpPlans && !!userPolicy.perks.esim;
  const showHomeScreenBottomBanner = isMobile && (isAndroid || isIOS);

  const isMobileView = useBreakpointValue({ base: true, md: false });
  const isDesktopView = useBreakpointValue({ base: false, md: true });
  const isLargeDesktopView = useBreakpointValue({ base: false, xl: true });

  const canAddJourney = userPolicy.dashboard?.canAddTrip;
  const required24hDepartureTime = userPolicy.benefits?.required24hDepartureTime;

  const showPerkServices = !!esim || !!carRental || !!airportTransfer;

  const { openZowieChat } = useZowie();

  const handleFaqClick = () => {
    trackCtaClick("see faq", "home page");
  };

  const handleLoungeExperienceTileClick = () => {
    trackCtaClick("your available lounge passes", "home page");
    navigate(generatePath(PAGE_PATHS.LOUNGES_PAGE));
  };

  const handleSupportClick = () => {
    openZowieChat();
    trackCtaClick("need help contact support", "home page");
  };

  const actionCardAltStyling = {
    height: "100%",
    flexDirection: isMobileView || (isLargeDesktopView && showESimBanner) ? "column" : "row",
    sx: {
      ".icon": {
        alignSelf: "flex-start",
      },
    },
  };

  return (
    <Box width="100%" mb={{ base: 10, md: "unset" }}>
      <Heading fontSize={{ base: "2xl", md: "3xl" }} mt={{ base: 4, md: 2 }} mb={{ base: 2, md: 12 }}>
        {userProfile.firstName
          ? t("dashboard.welcome_message", { name: userProfile.firstName })
          : t("dashboard.welcome_message_no_name")}
      </Heading>
      <HowCanWeHelp userProfile={userProfile} canAddJourney={canAddJourney} />
      <VStack width="100%" spacing={12} align="stretch">
        {showMembershipEssentialUpsell && <UpsellEssential />}
        {showLoungeExperienceTile && (
          <LoungeExperienceTile loungePasses={loungePasses} handleClick={handleLoungeExperienceTileClick} />
        )}
        <JourneysLatest
          journeys={journeys}
          canAddJourney={canAddJourney}
          required24hDepartureTime={required24hDepartureTime}
          showUpsell={!userPolicy.coverage.active}
          order={userPolicy.coverage.covered ? 1 : 2}
        />
        <ClaimsLatest claimEnquiries={claims} order={userPolicy.coverage.covered ? 2 : 1} />
        {newAhpPlans ? (
          showPerkServices && <PerkServices userPolicy={userPolicy} />
        ) : (
          <VStack align="start" spacing={0} order={3}>
            <Flex
              flexWrap={{ base: "wrap", xl: "nowrap" }}
              justifyContent="space-between"
              columnGap={4}
              rowGap={12}
              width="100%"
              height="100%"
              mt={(isMobileView || isDesktopView) && showESimBanner ? 7 : 0}
            >
              {showESimBanner && <ESimBanner userProfile={userProfile} eventPlacement="dashboard" />}
              <Flex
                columnGap={4}
                rowGap={12}
                width={{ base: "100%", lg: "50%" }}
                flex="1 1 50%"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="stretch"
              >
                <ActionCard
                  header={t("dashboard.faqs")}
                  description={t("dashboard.answer_questions")}
                  handleClick={handleFaqClick}
                  imageSrc={faqFormal}
                  externalLink={airhelpUtils.websiteUrls("faqPath", locale)}
                  {...actionCardAltStyling}
                />
                {showSupport && (
                  <ActionCard
                    header={t("dashboard.need_help")}
                    description={t("dashboard.our_team_for_you")}
                    handleClick={handleSupportClick}
                    imageSrc={supportFormal}
                    {...actionCardAltStyling}
                  />
                )}
              </Flex>
            </Flex>
          </VStack>
        )}
        {showHomeScreenBottomBanner && (
          <Box order={4}>
            <AddToHomeScreenShortcutBottomBanner />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default HomeView;
